import React, { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { DownloadIcon, CalendarIcon, ClockIcon, DatabaseIcon } from 'lucide-react';
interface ExcelExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (dateFrom: string | null, dateTo: string | null) => void;
  title?: string;
  isExporting?: boolean;
}
export function ExcelExportModal({
  isOpen,
  onClose,
  onExport,
  title = 'Exportar a Excel',
  isExporting = false
}: ExcelExportModalProps) {
  const today = new Date().toISOString().split('T')[0];
  const [mode, setMode] = useState<'today' | 'range' | 'all'>('all');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const handleExport = () => {
    if (mode === 'all') {
      onExport(null, null);
    } else if (mode === 'today') {
      onExport(today, today);
    } else {
      onExport(dateFrom, dateTo);
    }
  };
  const setQuickRange = (type: 'week' | 'month' | '3months' | '6months') => {
    const end = new Date();
    const start = new Date();
    switch (type) {
      case 'week':
        start.setDate(end.getDate() - 7);
        break;
      case 'month':
        start.setMonth(end.getMonth() - 1);
        break;
      case '3months':
        start.setMonth(end.getMonth() - 3);
        break;
      case '6months':
        start.setMonth(end.getMonth() - 6);
        break;
    }
    setDateFrom(start.toISOString().split('T')[0]);
    setDateTo(end.toISOString().split('T')[0]);
    setMode('range');
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm" icon={<DownloadIcon className="w-5 h-5" data-id="element-4412" />} accentColor="sky" data-id="element-4411">
      <div className="space-y-5" data-id="element-4413">
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4414">
          Selecciona el rango de fechas para los datos a exportar.
        </p>

        {/* Mode Selection */}
        <div className="grid grid-cols-3 gap-2" data-id="element-4415">
          <button type="button" onClick={() => setMode('all')} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${mode === 'all' ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400'}`} data-id="element-4416">
            <DatabaseIcon className="w-5 h-5" data-id="element-4417" />
            <span className="text-xs font-semibold" data-id="element-4418">Todos</span>
          </button>
          <button type="button" onClick={() => setMode('today')} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${mode === 'today' ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400'}`} data-id="element-4419">
            <ClockIcon className="w-5 h-5" data-id="element-4420" />
            <span className="text-xs font-semibold" data-id="element-4421">Hoy</span>
          </button>
          <button type="button" onClick={() => setMode('range')} className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${mode === 'range' ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-600 dark:text-gray-400'}`} data-id="element-4422">
            <CalendarIcon className="w-5 h-5" data-id="element-4423" />
            <span className="text-xs font-semibold" data-id="element-4424">Rango</span>
          </button>
        </div>

        {/* Date Range Inputs */}
        {mode === 'range' && <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-900/30 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-4425">
            {/* Quick Range Buttons */}
            <div className="flex flex-wrap gap-2" data-id="element-4426">
              {[{
            label: 'Última semana',
            type: 'week' as const
          }, {
            label: 'Último mes',
            type: 'month' as const
          }, {
            label: '3 meses',
            type: '3months' as const
          }, {
            label: '6 meses',
            type: '6months' as const
          }].map(item => <button key={item.type} type="button" onClick={() => setQuickRange(item.type)} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-sky-300 dark:hover:border-sky-700 hover:text-sky-600 dark:hover:text-sky-400 transition-colors" data-id="element-4427">
                  {item.label}
                </button>)}
            </div>

            <div className="grid grid-cols-2 gap-3" data-id="element-4428">
              <Input type="date" label="Desde" value={dateFrom} onChange={e => setDateFrom(e.target.value)} max={dateTo || today} data-id="element-4429" />
              <Input type="date" label="Hasta" value={dateTo} onChange={e => setDateTo(e.target.value)} max={today} data-id="element-4430" />
            </div>
          </div>}

        {/* Summary */}
        <div className="p-3 bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800 rounded-lg" data-id="element-4431">
          <p className="text-sm text-sky-700 dark:text-sky-300 font-medium" data-id="element-4432">
            {mode === 'all' && '📊 Se exportarán todos los registros sin filtro de fecha.'}
            {mode === 'today' && `📅 Se exportarán solo los registros de hoy (${today}).`}
            {mode === 'range' && `📅 Se exportarán registros del ${dateFrom} al ${dateTo}.`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2" data-id="element-4433">
          <Button variant="outline" onClick={onClose} data-id="element-4434">
            Cancelar
          </Button>
          <Button onClick={handleExport} isLoading={isExporting} leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-4436" />} data-id="element-4435">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </div>
    </Modal>;
}